import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { endpointAPI } from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { toast } from 'react-hot-toast';

interface EndpointMetrics {
  cpu_usage_percent: number | null;
  cpu_temperature: number | null;
  memory_usage_percent: number | null;
  disk_usage_percent: number | null;
  battery_percent: number | null;
}

interface Endpoint {
  id: number;
  endpoint_id: string;
  name: string;
  asset_name?: string;
  os: string;
  os_version?: string;
  make?: string;
  model?: string;
  hostname?: string;
  primary_ip?: string;
  agent_version?: string;
  status: 'online' | 'offline' | 'pending';
  last_seen_at?: string;
  open_threats?: number;
  high_threats?: number;
  current_metrics?: EndpointMetrics | null;
}

interface Summary { total: number; online: number; offline: number; pending: number; open_threats?: number; at_risk?: number; }

const osIcon = (os: string): string => {
  const o = (os || '').toLowerCase();
  if (o.includes('win')) return 'bi-windows';
  if (o.includes('mac') || o.includes('darwin')) return 'bi-apple';
  if (o.includes('linux') || o.includes('ubuntu') || o.includes('debian')) return 'bi-ubuntu';
  return 'bi-pc-display';
};

const statusBadge = (status: string) => {
  switch (status) {
    case 'online': return { cls: 'bg-success', label: 'Online' };
    case 'offline': return { cls: 'bg-secondary', label: 'Offline' };
    default: return { cls: 'bg-warning text-dark', label: 'Pending' };
  }
};

const metricColor = (v: number | null | undefined): string => {
  if (v == null) return 'text-muted';
  if (v >= 90) return 'text-danger';
  if (v >= 75) return 'text-warning';
  return 'text-success';
};

const EndpointsListPage: React.FC = () => {
  useDocumentTitle('Endpoints');
  const navigate = useNavigate();
  const { currentAsset } = useAuth();
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  const [summary, setSummary] = useState<Summary>({ total: 0, online: 0, offline: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = async () => {
    if (!currentAsset) return;
    try {
      const res = await endpointAPI.list({ asset_id: currentAsset.id });
      if (res.data.status === 'success') {
        setEndpoints(res.data.data.endpoints || []);
        setSummary(res.data.data.summary || { total: 0, online: 0, offline: 0, pending: 0 });
      }
    } catch (error: any) {
      if (error.response?.status !== 401) console.error('Failed to load endpoints:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    // Live refresh every 15s so status/metrics stay current.
    pollRef.current = setInterval(load, 15000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAsset]);

  const filtered = statusFilter === 'all' ? endpoints : endpoints.filter(e => e.status === statusFilter);

  const runCommand = async (e: React.MouseEvent, id: number, cmd: string, label: string) => {
    e.stopPropagation();
    if (cmd === 'reboot' && !window.confirm('Reboot this endpoint now?')) return;
    try {
      await endpointAPI.issueCommand(id, cmd);
      toast.success(`${label} queued — runs when the endpoint next checks in`);
    } catch {
      toast.error(`Failed to queue ${label.toLowerCase()}`);
    }
  };

  const runFleetCommand = async (cmd: string, label: string) => {
    if (!currentAsset) return;
    if (!window.confirm(`${label} on all ${endpoints.length} endpoint(s) of ${currentAsset.name}?`)) return;
    try {
      const res = await endpointAPI.issueFleetCommand(currentAsset.id, cmd);
      toast.success(res.data.data?.message || `${label} queued for the fleet`);
    } catch {
      toast.error(`Failed to queue fleet ${label.toLowerCase()}`);
    }
  };

  return (
    <DashboardLayout>
      <div className="container-fluid py-3">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div>
            <h3 className="mb-1"><i className="bi bi-pc-display me-2"></i>Endpoints</h3>
            <p className="text-muted mb-0">Manage the computers running the CloudAyn agent for {currentAsset?.name || 'this asset'}.</p>
          </div>
          <div className="d-flex gap-2">
            {endpoints.length > 0 && (
              <div className="dropdown">
                <button className="btn btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown">
                  <i className="bi bi-lightning-charge me-1"></i>Fleet Actions
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li><button className="dropdown-item" onClick={() => runFleetCommand('run_scan', 'Scan')}><i className="bi bi-search me-2"></i>Scan all</button></li>
                  <li><button className="dropdown-item" onClick={() => runFleetCommand('system_update', 'Update')}><i className="bi bi-download me-2"></i>Update all</button></li>
                  <li><button className="dropdown-item" onClick={() => runFleetCommand('agent_update', 'Agent update')}><i className="bi bi-arrow-repeat me-2"></i>Update agent on all</button></li>
                  <li><hr className="dropdown-divider" /></li>
                  <li><button className="dropdown-item text-danger" onClick={() => runFleetCommand('reboot', 'Reboot')}><i className="bi bi-arrow-clockwise me-2"></i>Reboot all</button></li>
                </ul>
              </div>
            )}
            <button className="btn btn-primary" onClick={() => navigate('/endpoints/add')}>
              <i className="bi bi-plus-circle me-2"></i>Add Endpoint
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="row g-3 mb-4">
          {[
            { label: 'Total', value: summary.total, icon: 'bi-pc-display', color: 'text-primary', link: undefined as string | undefined },
            { label: 'Online', value: summary.online, icon: 'bi-check-circle', color: 'text-success', link: undefined },
            { label: 'At risk', value: summary.at_risk || 0, icon: 'bi-exclamation-triangle', color: 'text-warning', link: '/endpoints/threats' },
            { label: 'Open threats', value: summary.open_threats || 0, icon: 'bi-shield-exclamation', color: (summary.open_threats || 0) > 0 ? 'text-danger' : 'text-success', link: '/endpoints/threats' },
          ].map(card => (
            <div className="col-6 col-md-3" key={card.label}>
              <div className="card h-100" style={card.link ? { cursor: 'pointer' } : undefined}
                onClick={() => card.link && navigate(card.link)}>
                <div className="card-body d-flex align-items-center justify-content-between">
                  <div>
                    <div className={`fs-3 fw-bold ${card.color}`}>{card.value}</div>
                    <div className="text-muted small">{card.label}</div>
                  </div>
                  <i className={`bi ${card.icon} fs-2 ${card.color} opacity-50`}></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="btn-group mb-3">
          {['all', 'online', 'offline', 'pending'].map(f => (
            <button key={f}
              className={`btn btn-sm ${statusFilter === f ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setStatusFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
        ) : filtered.length === 0 ? (
          <div className="card"><div className="card-body text-center py-5">
            <i className="bi bi-pc-display fs-1 text-muted d-block mb-3"></i>
            <h5>No endpoints yet</h5>
            <p className="text-muted">Add an endpoint by name, then run the install command on that computer.</p>
            <button className="btn btn-primary" onClick={() => navigate('/endpoints/add')}>
              <i className="bi bi-plus-circle me-2"></i>Add your first endpoint
            </button>
          </div></div>
        ) : (
          <div className="row g-3">
            {filtered.map(ep => {
              const badge = statusBadge(ep.status);
              const m = ep.current_metrics;
              return (
                <div className="col-12 col-md-6 col-xl-4" key={ep.id}>
                  <div className="card h-100 endpoint-card" style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/endpoints/${ep.id}`)}>
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="d-flex align-items-center gap-2">
                          <i className={`bi ${osIcon(ep.os)} fs-4`}></i>
                          <div>
                            <div className="fw-semibold">{ep.name}</div>
                            <div className="small text-muted">{ep.make || ep.hostname || ep.os || 'Unknown device'}{ep.model ? ` · ${ep.model}` : ''}</div>
                          </div>
                        </div>
                        <div className="d-flex flex-column align-items-end gap-1">
                          <span className={`badge ${badge.cls}`}>{badge.label}</span>
                          {(ep.open_threats || 0) > 0 && (
                            <span className={`badge ${(ep.high_threats || 0) > 0 ? 'bg-danger' : 'bg-warning text-dark'}`}>
                              <i className="bi bi-shield-exclamation me-1"></i>{ep.open_threats}
                            </span>
                          )}
                        </div>
                      </div>

                      {m ? (
                        <div className="row text-center g-2 mt-2">
                          <div className="col-3">
                            <div className={`fw-bold ${metricColor(m.cpu_usage_percent)}`}>{m.cpu_usage_percent != null ? `${Math.round(m.cpu_usage_percent)}%` : '—'}</div>
                            <div className="small text-muted">CPU</div>
                          </div>
                          <div className="col-3">
                            <div className={`fw-bold ${metricColor(m.memory_usage_percent)}`}>{m.memory_usage_percent != null ? `${Math.round(m.memory_usage_percent)}%` : '—'}</div>
                            <div className="small text-muted">RAM</div>
                          </div>
                          <div className="col-3">
                            <div className={`fw-bold ${metricColor(m.disk_usage_percent)}`}>{m.disk_usage_percent != null ? `${Math.round(m.disk_usage_percent)}%` : '—'}</div>
                            <div className="small text-muted">Disk</div>
                          </div>
                          <div className="col-3">
                            <div className="fw-bold text-info">{m.cpu_temperature ? `${Math.round(m.cpu_temperature)}°` : '—'}</div>
                            <div className="small text-muted">Temp</div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-muted small mt-2">
                          {ep.status === 'pending' ? 'Waiting for the agent to check in…' : 'No metrics yet'}
                        </div>
                      )}

                      <div className="d-flex gap-2 mt-3">
                        <button className="btn btn-sm btn-outline-secondary flex-fill"
                          disabled={ep.status !== 'online'}
                          onClick={(e) => runCommand(e, ep.id, 'reboot', 'Reboot')}>
                          <i className="bi bi-arrow-clockwise me-1"></i>Reboot
                        </button>
                        <button className="btn btn-sm btn-outline-secondary flex-fill"
                          disabled={ep.status !== 'online'}
                          onClick={(e) => runCommand(e, ep.id, 'system_update', 'Update')}>
                          <i className="bi bi-download me-1"></i>Update
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default EndpointsListPage;
